/* RecipesPanel — Admin tab listing EVERY recipe across all users.
 *
 * Features:
 *   - Search by title or creator
 *   - Filter by privacy (public/private/all) + by cookbook (dropdown)
 *   - Sort by newest / oldest / title A→Z / Z→A
 *   - Pagination (100 per page)
 *   - Multi-select checkboxes per row + select-all
 *   - Bulk Delete (confirm dialog)
 *   - Bulk Move (modal to choose target cookbook from /admin/cookbooks/all)
 *   - Click "Edit" → full owner-style edit page (/r/{slug-id}/edit) — admin
 *     bypass is in /api/admin/recipes/{id} but the page itself shells out to
 *     the existing PATCH route which, for admins, falls through to admin PATCH
 *     server-side. We pass `admin=1` to switch the edit UI into admin mode.
 *
 * Backend endpoints used:
 *   GET    /api/admin/recipes/all
 *   PATCH  /api/admin/recipes/{id}           — used by the linked edit page
 *   POST   /api/admin/recipes/bulk-delete
 *   POST   /api/admin/recipes/bulk-move
 *   GET    /api/admin/cookbooks/all          — destination dropdown
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { MagnifyingGlass, Trash, ArrowsLeftRight, PencilSimple, ArrowSquareOut, ShieldCheck, Sparkle } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { recipePath } from "@/lib/slug";
import SnippetPreview from "@/components/SnippetPreview";

const PAGE = 100;

export default function RecipesPanel() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [skip, setSkip] = useState(0);
  const [q, setQ] = useState("");
  const [privacy, setPrivacy] = useState("");
  const [cookbookFilter, setCookbookFilter] = useState("");
  const [sort, setSort] = useState("newest");
  const [selected, setSelected] = useState(new Set());
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState("");
  const [moveBusy, setMoveBusy] = useState(false);
  const [cookbooks, setCookbooks] = useState([]);
  /* SEO editor state — one recipe at a time. `seoRow` holds a shallow
   * copy of the recipe row so edits don't mutate the underlying list. */
  const [seoRow, setSeoRow] = useState(null);
  const [seoBusy, setSeoBusy] = useState(false);

  /* Load the full recipe (with existing SEO overrides) into the editor. The
   * admin/recipes/all endpoint returns a lightweight projection so we
   * refetch to get seo_title / seo_description / image_alt.
   *
   * NOTE: pass `?fresh=1` so the service worker bypasses its recipe cache.
   * Without this, opening the SEO dialog immediately after saving returned
   * the STALE cached recipe (no SEO fields) instead of the freshly-patched
   * version. See public/service-worker.js — the SW respects `?fresh=` as
   * an explicit cache-bypass signal. */
  const openSeo = async (r) => {
    try {
      const { data } = await api.get(`/recipes/${r.recipe_id}?fresh=1`);
      setSeoRow({
        recipe_id: r.recipe_id,
        title: data.title || r.title || "",
        image_url: (data.ai_image_active && data.ai_image_url) ? data.ai_image_url : (data.images || [])[0] || "",
        seo_title: data.seo_title || "",
        seo_description: data.seo_description || "",
        seo_keywords: data.seo_keywords || "",
        image_alt: data.image_alt || "",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load recipe");
    }
  };

  const saveSeo = async () => {
    if (!seoRow) return;
    setSeoBusy(true);
    try {
      await api.patch(`/admin/recipes/${seoRow.recipe_id}`, {
        seo_title: seoRow.seo_title || "",
        seo_description: seoRow.seo_description || "",
        seo_keywords: seoRow.seo_keywords || "",
        image_alt: seoRow.image_alt || "",
      });
      toast.success("SEO overrides saved — live on next page load");
      setSeoRow(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSeoBusy(false); }
  };

  /* Ask Claude (via Emergent LLM key) to draft optimised SEO fields for
   * the currently-open recipe. Populates the inputs — admin still reviews
   * and saves manually. Non-destructive: does not overwrite fields the
   * admin has already customised in this session. */
  const [seoSuggestBusy, setSeoSuggestBusy] = useState(false);
  const suggestSeo = async () => {
    if (!seoRow) return;
    setSeoSuggestBusy(true);
    try {
      const { data } = await api.post(`/admin/recipes/${seoRow.recipe_id}/suggest-seo`);
      if (!data || (!data.seo_title && !data.seo_description && !data.image_alt && !data.seo_keywords)) {
        toast.error("AI didn't return a suggestion — try again in a moment");
        return;
      }
      setSeoRow((prev) => ({
        ...prev,
        seo_title: data.seo_title || prev.seo_title,
        seo_description: data.seo_description || prev.seo_description,
        seo_keywords: data.seo_keywords || prev.seo_keywords,
        image_alt: data.image_alt || prev.image_alt,
      }));
      toast.success("AI suggestions loaded — review and save");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI suggestion failed");
    } finally { setSeoSuggestBusy(false); }
  };

  const reload = async (resetSkip = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (privacy) params.set("privacy", privacy);
      if (cookbookFilter) params.set("cookbook_id", cookbookFilter);
      params.set("sort", sort);
      params.set("limit", String(PAGE));
      params.set("skip", String(resetSkip ? 0 : skip));
      const { data } = await api.get(`/admin/recipes/all?${params.toString()}`);
      setRows(data.rows || []);
      setTotal(data.total || 0);
      if (resetSkip) setSkip(0);
      setSelected(new Set()); // clear selection on any filter change
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load recipes");
    } finally { setLoading(false); }
  };

  // Initial + cookbooks list for filter/move dropdowns
  useEffect(() => {
    reload(true);
    api.get("/admin/cookbooks/all").then(({ data }) => setCookbooks(data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when sort/privacy/cookbookFilter changes
  useEffect(() => { reload(true); /* eslint-disable-line */ }, [privacy, cookbookFilter, sort]);
  // Re-fetch when paging
  useEffect(() => { reload(false); /* eslint-disable-line */ }, [skip]);

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.recipe_id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.recipe_id)));
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Permanently delete ${ids.length} recipe${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/admin/recipes/bulk-delete", { recipe_ids: ids });
      toast.success(`Deleted ${data.deleted} recipe${data.deleted === 1 ? "" : "s"}`);
      setSelected(new Set());
      await reload(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk delete failed"); }
  };

  const bulkMove = async () => {
    const ids = Array.from(selected);
    if (!ids.length || !moveTarget) return;
    setMoveBusy(true);
    try {
      const { data } = await api.post("/admin/recipes/bulk-move", {
        recipe_ids: ids,
        target_cookbook_id: moveTarget,
      });
      toast.success(`Moved ${data.moved} recipe${data.moved === 1 ? "" : "s"}`);
      setMoveOpen(false);
      setMoveTarget("");
      setSelected(new Set());
      await reload(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk move failed");
    } finally { setMoveBusy(false); }
  };

  const cookbooksById = useMemo(() => {
    const m = {};
    cookbooks.forEach((c) => { m[c.cookbook_id] = c; });
    return m;
  }, [cookbooks]);

  return (
    <div data-testid="admin-recipes-panel">
      <p className="overline mb-2">Moderation</p>
      <h2 className="font-display text-3xl font-black text-rf-ink mb-1">All recipes</h2>
      <p className="text-rf-ink-soft mb-6 text-sm max-w-3xl">
        Every recipe across every user. Search, filter, edit text + imagery, or bulk-move / bulk-delete from here.
      </p>

      {/* Site-wide AI nutrition backfill — queues a background job that
          hits the LLM for every recipe missing a nutrition estimate (ALL
          users, not just the admin's own cookbooks). Live progress badge
          while the job runs; one-click re-poll if the page is refreshed. */}
      <SiteWideNutritionBackfillCard />

      {/* Filters row */}
      <div className="card-recipe !p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px] relative">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-rf-ink-soft" size={18} />
          <input
            data-testid="admin-recipes-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && reload(true)}
            placeholder="Search by title or creator…"
            className="input-rf pl-10"
          />
        </div>
        <select
          data-testid="admin-recipes-privacy"
          value={privacy}
          onChange={(e) => setPrivacy(e.target.value)}
          className="input-rf max-w-[160px]"
        >
          <option value="">All visibility</option>
          <option value="public">Public only</option>
          <option value="private">Private only</option>
        </select>
        <select
          data-testid="admin-recipes-cookbook"
          value={cookbookFilter}
          onChange={(e) => setCookbookFilter(e.target.value)}
          className="input-rf max-w-[280px]"
        >
          <option value="">All cookbooks</option>
          {cookbooks.map((c) => (
            <option key={c.cookbook_id} value={c.cookbook_id}>
              {c.title || "Untitled"} {c.owner_email ? `— ${c.owner_email}` : ""}
            </option>
          ))}
        </select>
        <select
          data-testid="admin-recipes-sort"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="input-rf max-w-[160px]"
        >
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="title_az">Title A→Z</option>
          <option value="title_za">Title Z→A</option>
        </select>
        <button
          data-testid="admin-recipes-search-btn"
          onClick={() => reload(true)}
          className="btn-outline-ink !py-2 !px-4 text-sm"
        >
          Search
        </button>
      </div>

      {/* Bulk action bar — only when something is selected */}
      {selected.size > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3 p-3 rounded-2xl bg-rf-amber/15 border border-rf-amber/40" data-testid="admin-recipes-bulk-bar">
          <span className="text-sm font-bold text-rf-ink">
            {selected.size} selected
          </span>
          <button
            data-testid="admin-recipes-bulk-move"
            onClick={() => setMoveOpen(true)}
            className="btn-outline-ink !py-2 !px-3 text-xs inline-flex items-center gap-1"
          >
            <ArrowsLeftRight size={14} weight="bold" /> Move to cookbook…
          </button>
          <button
            data-testid="admin-recipes-bulk-delete"
            onClick={bulkDelete}
            className="btn-amber !bg-red-600 hover:!bg-red-700 !py-2 !px-3 text-xs inline-flex items-center gap-1"
          >
            <Trash size={14} weight="bold" /> Delete {selected.size}
          </button>
        </div>
      )}

      {/* Table
        * Explicit column widths + `min-w-[1180px]` mean the layout stays
        * predictable at any screen size: on a narrow laptop it horizontally
        * scrolls (parent has overflow-x-auto) instead of squishing the
        * actions column so the SEO / Edit / View buttons hide off the
        * right edge. `table-fixed` + <colgroup> lock the widths regardless
        * of content length. */}
      <div className="card-recipe !p-0 overflow-x-auto">
        <table className="w-full min-w-[1180px] text-sm table-fixed" data-testid="admin-recipes-table">
          <colgroup>
            <col style={{ width: "40px" }} />       {/* select */}
            <col style={{ width: "300px" }} />      {/* title */}
            <col style={{ width: "220px" }} />      {/* owner */}
            <col style={{ width: "180px" }} />      {/* cookbook */}
            <col style={{ width: "110px" }} />      {/* visibility */}
            <col style={{ width: "100px" }} />      {/* updated */}
            <col style={{ width: "230px" }} />      {/* actions */}
          </colgroup>
          <thead className="bg-rf-cream-alt sticky top-0">
            <tr className="text-left text-xs font-black uppercase tracking-wider text-rf-ink-soft">
              <th className="p-3">
                <input
                  data-testid="admin-recipes-select-all"
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  className="w-4 h-4"
                />
              </th>
              <th className="p-3">Title</th>
              <th className="p-3">Owner</th>
              <th className="p-3">Cookbook</th>
              <th className="p-3">Visibility</th>
              <th className="p-3">Updated</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="p-6 text-center text-rf-ink-soft italic">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-rf-ink-soft italic">No recipes match.</td></tr>
            )}
            {!loading && rows.map((r) => {
              const cb = cookbooksById[r.cookbook_id];
              return (
                <tr
                  key={r.recipe_id}
                  className={`border-t border-rf-ink/10 ${selected.has(r.recipe_id) ? "bg-rf-amber/10" : ""}`}
                  data-testid={`admin-recipe-row-${r.recipe_id}`}
                >
                  <td className="p-3">
                    <input
                      data-testid={`admin-recipe-select-${r.recipe_id}`}
                      type="checkbox"
                      checked={selected.has(r.recipe_id)}
                      onChange={() => toggleOne(r.recipe_id)}
                      className="w-4 h-4"
                    />
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      {r.images?.[0] && (
                        <img
                          src={r.images[0]}
                          alt=""
                          className="w-10 h-10 rounded-md object-cover shrink-0 border border-rf-ink/10"
                          loading="lazy"
                        />
                      )}
                      <div className="min-w-0">
                        <p className="font-display font-extrabold text-rf-ink truncate" title={r.title}>{r.title || "Untitled"}</p>
                        {r.creator_name && <p className="text-[11px] text-rf-ink-soft truncate">by {r.creator_name}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-rf-ink-soft text-xs">
                    <p className="truncate" title={r.owner_email}>{r.owner_email || "—"}</p>
                  </td>
                  <td className="p-3 text-rf-ink-soft text-xs">
                    <p className="truncate" title={r.cookbook_title}>{r.cookbook_title || "—"}</p>
                  </td>
                  <td className="p-3 text-[11px]">
                    {r.is_public ? (
                      <span className="font-bold uppercase tracking-wider text-emerald-700">Public</span>
                    ) : (
                      <span className="font-bold uppercase tracking-wider text-rf-ink-soft">Private</span>
                    )}
                    {r.is_secret && <span className="block text-[10px] text-rf-orange font-bold uppercase tracking-wider">Secret</span>}
                  </td>
                  <td className="p-3 text-rf-ink-soft text-xs whitespace-nowrap">
                    {r.updated_at ? new Date(r.updated_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => openSeo(r)}
                      data-testid={`admin-recipe-seo-${r.recipe_id}`}
                      className="inline-flex items-center gap-1 text-xs font-bold text-rf-orange hover:underline mr-3"
                      title="Edit SEO title, description and image alt"
                    >
                      <ShieldCheck size={12} weight="bold" /> SEO
                    </button>
                    <Link
                      to={`${recipePath(r)}/edit?admin=1`}
                      data-testid={`admin-recipe-edit-${r.recipe_id}`}
                      className="inline-flex items-center gap-1 text-xs font-bold text-rf-orange hover:underline mr-3"
                    >
                      <PencilSimple size={12} weight="bold" /> Edit
                    </Link>
                    <Link
                      to={recipePath(r)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`admin-recipe-view-${r.recipe_id}`}
                      className="inline-flex items-center gap-1 text-xs font-bold text-rf-ink-soft hover:text-rf-ink"
                    >
                      <ArrowSquareOut size={12} weight="bold" /> View
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-rf-ink-soft">
          Showing <b>{rows.length}</b> of <b>{total}</b>
        </p>
        <div className="flex gap-2">
          <button
            disabled={skip === 0}
            onClick={() => setSkip(Math.max(0, skip - PAGE))}
            className="btn-outline-ink !py-1.5 !px-3 text-xs disabled:opacity-30"
          >
            ← Previous
          </button>
          <button
            disabled={skip + PAGE >= total}
            onClick={() => setSkip(skip + PAGE)}
            className="btn-outline-ink !py-1.5 !px-3 text-xs disabled:opacity-30"
          >
            Next →
          </button>
        </div>
      </div>

      {/* Bulk move modal */}
      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent className="bg-rf-cream border-rf-ink/10">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black text-rf-ink">
              Move {selected.size} recipe{selected.size === 1 ? "" : "s"}
            </DialogTitle>
            <DialogDescription className="text-sm text-rf-ink-soft">
              Choose a destination cookbook. The recipes&apos; owner will be updated to the destination owner — counters rebalance automatically.
            </DialogDescription>
          </DialogHeader>
          <select
            data-testid="admin-recipes-move-target"
            value={moveTarget}
            onChange={(e) => setMoveTarget(e.target.value)}
            className="input-rf w-full mb-4"
          >
            <option value="">— Pick a cookbook —</option>
            {cookbooks.map((c) => (
              <option key={c.cookbook_id} value={c.cookbook_id}>
                {c.title || "Untitled"} {c.owner_email ? `— ${c.owner_email}` : ""}
              </option>
            ))}
          </select>
          <button
            data-testid="admin-recipes-move-confirm"
            onClick={bulkMove}
            disabled={!moveTarget || moveBusy}
            className="btn-amber w-full !rounded-xl"
          >
            {moveBusy ? "Moving…" : "Move recipes"}
          </button>
        </DialogContent>
      </Dialog>

      {/* SEO override editor — admin-only. Lets any admin set a custom
          <title>, meta description and hero image alt on any recipe
          without touching its owner-facing edit page. Empty field = revert
          to the auto-generated default. */}
      <Dialog open={!!seoRow} onOpenChange={(o) => !o && setSeoRow(null)}>
        <DialogContent className="bg-rf-cream border-rf-ink/10 max-w-lg" data-testid="admin-recipe-seo-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black text-rf-ink">
              SEO overrides
            </DialogTitle>
            <DialogDescription className="text-sm text-rf-ink-soft">
              Editing <b className="text-rf-ink">{seoRow?.title || "recipe"}</b>. Live on the public page immediately after save.
            </DialogDescription>
          </DialogHeader>
          {seoRow && (
            <div className="space-y-4">
              {/* AI drafter — one click populates all three fields with a
                  Claude-generated suggestion. Admin still reviews and clicks
                  Save. Non-destructive if the response is empty. */}
              <button
                type="button"
                onClick={suggestSeo}
                disabled={seoSuggestBusy || seoBusy}
                data-testid="admin-recipe-seo-suggest"
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-rf-orange/40 hover:border-rf-orange text-rf-orange hover:bg-rf-orange/5 font-black text-sm py-2.5 transition-colors disabled:opacity-60"
              >
                <Sparkle size={16} weight="fill" />
                {seoSuggestBusy ? "Drafting with AI…" : "Suggest with AI"}
              </button>
              <div>
                <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">SEO title (page &lt;title&gt;)</label>
                <input
                  type="text"
                  className="input-rf w-full"
                  placeholder={`e.g. ${seoRow.title} — Family Recipe`}
                  maxLength={110}
                  value={seoRow.seo_title}
                  onChange={(e) => setSeoRow({ ...seoRow, seo_title: e.target.value })}
                  data-testid="admin-recipe-seo-title-input"
                />
                <p className="text-[10px] text-rf-ink-soft mt-1">{seoRow.seo_title.length} / 110 · leave empty to use recipe title</p>
              </div>
              <div>
                <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">Meta description (search snippet)</label>
                <textarea
                  rows={3}
                  className="input-rf w-full"
                  placeholder="Short, human-readable 150–160 char summary that appears in Google/Bing search results."
                  maxLength={200}
                  value={seoRow.seo_description}
                  onChange={(e) => setSeoRow({ ...seoRow, seo_description: e.target.value })}
                  data-testid="admin-recipe-seo-description-input"
                />
                <p className="text-[10px] text-rf-ink-soft mt-1">{seoRow.seo_description.length} / 200 · leave empty to auto-generate from recipe body</p>
              </div>
              <div>
                <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">Hero image alt text</label>
                <input
                  type="text"
                  className="input-rf w-full"
                  placeholder={`e.g. "A close-up of ${(seoRow.title || "the dish").toLowerCase()} on a wooden table"`}
                  maxLength={150}
                  value={seoRow.image_alt}
                  onChange={(e) => setSeoRow({ ...seoRow, image_alt: e.target.value })}
                  data-testid="admin-recipe-seo-image-alt-input"
                />
                <p className="text-[10px] text-rf-ink-soft mt-1">Improves accessibility + image search ranking. Leave empty to use the recipe title.</p>
              </div>
              <div>
                <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">Keywords (comma-separated, 10–14 terms)</label>
                <textarea
                  rows={2}
                  className="input-rf w-full"
                  placeholder="chicken curry recipe, easy weeknight supper, one-pot chicken, UK curry, family recipe, kid-friendly curry"
                  maxLength={400}
                  value={seoRow.seo_keywords || ""}
                  onChange={(e) => setSeoRow({ ...seoRow, seo_keywords: e.target.value })}
                  data-testid="admin-recipe-seo-keywords-input"
                />
                <p className="text-[10px] text-rf-ink-soft mt-1">Google ignores this tag, but Bing, DuckDuckGo, Yandex and AI search engines (ChatGPT, Perplexity, Google AI Overviews) still read it.</p>
              </div>
              <SnippetPreview
                title={seoRow.seo_title || seoRow.title}
                description={seoRow.seo_description || ""}
                url={`https://recipefiler.com/r/${seoRow.recipe_id}`}
                image={seoRow.image_url || null}
                brand="Recipe Filer"
                dataTestidPrefix="snippet-preview-recipe"
              />
              <button
                type="button"
                onClick={saveSeo}
                disabled={seoBusy}
                data-testid="admin-recipe-seo-save"
                className="btn-amber w-full inline-flex items-center justify-center gap-2 !rounded-xl disabled:opacity-60"
              >
                <ShieldCheck size={16} weight="bold" /> {seoBusy ? "Saving…" : "Save SEO overrides"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


/**
 * SiteWideNutritionBackfillCard — top-of-Admin-Recipes action that queues
 * an LLM nutrition pass for every recipe missing one, across the ENTIRE
 * site (all users, all cookbooks). Separate from the Dashboard button
 * which is scoped to the admin's own cookbooks.
 *
 * Progress polling matches the summary backfill flow (3s cadence). On
 * refresh, the latest job id is stashed in localStorage so re-visits can
 * resume polling instead of showing a stale "idle" state.
 */
function SiteWideNutritionBackfillCard() {
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    if (!window.confirm("AI-estimate per-serving nutrition for every recipe on the site that doesn't already have one? This will consume LLM credits proportional to the recipe count.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/recipes/backfill-nutrition", {});
      if (data.status === "noop") {
        toast(data.message || "Every recipe already has nutrition.");
        setBusy(false);
        return;
      }
      try { localStorage.setItem("rf_admin_nutr_job", data.job_id); } catch { /* quota */ }
      setJob({ ...data, processed: 0, inserted: 0, skipped: 0 });
      toast(`Site-wide nutrition estimation started — ${data.requested} recipe${data.requested === 1 ? "" : "s"} to go.`);
      poll(data.job_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start");
    } finally { setBusy(false); }
  };

  const poll = async (jobId) => {
    try {
      const { data: s } = await api.get(`/admin/recipes/backfill-nutrition/status/${jobId}`);
      setJob(s);
      if (s.status === "done") {
        toast.success(`Done — ${s.inserted || 0} written, ${s.skipped || 0} skipped.`);
        try { localStorage.removeItem("rf_admin_nutr_job"); } catch { /* ignore */ }
        return;
      }
      if (s.status === "error") { toast.error(s.error || "Backfill failed"); return; }
      if (s.status === "cancelled") return;
      setTimeout(() => poll(jobId), 3000);
    } catch (e) {
      // 404 means the job expired; clear and let the admin start a new one.
      if (e?.response?.status === 404) {
        try { localStorage.removeItem("rf_admin_nutr_job"); } catch { /* ignore */ }
        setJob(null);
      }
    }
  };

  // Resume polling an in-flight job after a page refresh.
  useEffect(() => {
    let saved = null;
    try { saved = localStorage.getItem("rf_admin_nutr_job"); } catch { /* ignore */ }
    if (saved) poll(saved);
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const running = job?.status === "running";

  return (
    <div
      className="card-recipe !p-4 mb-4 flex flex-wrap items-start gap-3 border-l-4 border-rf-orange"
      data-testid="admin-nutrition-backfill-card"
    >
      <div className="flex-1 min-w-[260px]">
        <h3 className="font-display font-black text-rf-ink text-base mb-0.5">AI nutrition — site-wide backfill</h3>
        <p className="text-xs text-rf-ink-soft leading-snug">
          Fills in a per-serving macro card on every recipe (any user, any cookbook) that doesn't already have one. New recipes get this automatically on create — this button is for the historic catalogue.
        </p>
        {running && (
          <p className="text-xs font-bold text-rf-orange mt-1" data-testid="admin-nutrition-backfill-progress">
            Running · {job.processed || 0} / {job.total || job.requested || 0} · {job.inserted || 0} written, {job.skipped || 0} skipped
          </p>
        )}
        {job?.status === "done" && (
          <p className="text-xs font-bold text-emerald-700 mt-1">
            Last run: {job.inserted || 0} written, {job.skipped || 0} skipped.
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={start}
        disabled={busy || running}
        data-testid="admin-nutrition-backfill-start"
        className="btn-amber !py-2.5 !px-4 text-sm disabled:opacity-60"
      >
        {running ? "Running…" : (busy ? "Starting…" : "Backfill nutrition for ALL recipes")}
      </button>
    </div>
  );
}
